"use strict";

var ActivityTracker = require("./ActivityTracker");
var Constants_1 = require("./Constants");
var IndexedTracker_1 = require("./IndexedTracker");
var SurveyStatCollectionEventActivity_1 = require("./SurveyStatCollectionEventActivity");
var Utils_1 = require("./Utils");
var queuedActivities = [];
var pendingActivityCount = 0;
var previousTrackedActivityMap = {};
/**
 * This class tracks log calls against named activity strings (matched on strict case-sensitive equality)
 * and calls the provided callback when all activity thresholds for a given survey have been crossed
 */
var SurveyActivityListener = /** @class */function () {
  function SurveyActivityListener(callback, loggerCallback) {
    if (!callback) {
      throw new Error("callback must not be null");
    }
    this.trackedActivityMap = {};
    this.callback = callback;
    this.loggerCallback = loggerCallback;
  }
  SurveyActivityListener.resetSessionActivity = function () {
    previousTrackedActivityMap = {};
  };
  SurveyActivityListener.prototype.logActivity = function (activityName, increment) {
    if (increment === void 0) {
      increment = 1;
    }
    this.logActivity_private(activityName, 0 /* Increment */, increment, null);
  };
  SurveyActivityListener.prototype.logActivityStartTime = function (activityName, startTime) {
    this.logActivity_private(activityName, 1 /* StartTime */, 0, startTime);
  };
  SurveyActivityListener.prototype.logActivityStopTime = function (activityName, stopTime) {
    this.logActivity_private(activityName, 2 /* StopTime */, 0, stopTime);
  };
  SurveyActivityListener.prototype.SetActivityTrackingContracts = function (activityTrackingContracts, baseline) {
    if (baseline === void 0) {
      baseline = new SurveyStatCollectionEventActivity_1.SurveyStatCollectionEventActivity();
    }
    var duplicateCounter = 0;
    if (!activityTrackingContracts || activityTrackingContracts.length === 0 || !baseline) {
      return;
    }
    // Populate a new map based on the passed in surveys, but propagating and tracking activity counts
    // we may have seen against those events
    // NOTE: We will lose activity counts for survey events that are no longer relevant.
    var newMap = {};
    var trackedSurveyIds = [];
    for (var _i = 0, activityTrackingContracts_1 = activityTrackingContracts; _i < activityTrackingContracts_1.length; _i++) {
      var activityTrackingContract = activityTrackingContracts_1[_i];
      var trackingSet = activityTrackingContract.trackingSet;
      // Added to the new set to make sure they are not duplicated. Based on MSO.
      if (trackedSurveyIds.indexOf(activityTrackingContract.surveyId) === -1) {
        trackedSurveyIds.push(activityTrackingContract.surveyId);
      } else {
        // Duplicate surveyId being added, count it for the log.
        duplicateCounter++;
        continue;
      }
      // At this point, the survey's activities are safe to add.
      // Set up the structures we need to transfer baseline counts (from previous sessions) or current counts (from previous trackers)
      // Both vectors must be sorted in the same order as the trackingSet.List items
      var baselineStats = baseline.getBySurveyId(activityTrackingContract.surveyId);
      var baselineCounts = [trackingSet.getList().length];
      var currentIndexedTrackers = new Array(trackingSet.getList().length);
      var currentBaselineIndex = 0;
      var currentIndex = 0;
      for (var _a = 0, _b = trackingSet.getList(); _a < _b.length; _a++) {
        var trackingData = _b[_a];
        // Baseline stats count is 0 for events with "IsAggregate = false"
        baselineCounts[currentIndex] = 0;
        if (trackingData.getIsAggregate() && baselineStats && currentBaselineIndex < baselineStats.Counts.length) {
          baselineCounts[currentIndex] = baselineStats.Counts[currentBaselineIndex++];
        }
        // Session stats may be available in the previous trackedActivityMap
        // Transfer the old "current session" count to the new tracker
        // Is set to null if not available
        if (previousTrackedActivityMap[trackingData.getActivity()]) {
          for (var _c = 0, _d = previousTrackedActivityMap[trackingData.getActivity()]; _c < _d.length; _c++) {
            var tracker = _d[_c];
            if (tracker.surveyId === activityTrackingContract.surveyId) {
              currentIndexedTrackers[currentIndex] = tracker;
              break;
            }
          }
        }
        currentIndex++;
      }
      // Make a new tracker and init the counts appropriately.
      var newTracker = new ActivityTracker(trackingSet);
      // If in a future change we start keeping trackers registered past activation, this will change
      newTracker.initCounts(baselineCounts, currentIndexedTrackers, false /*wasActivatedThisSession*/);
      // Setup the indexed trackers
      for (var _e = 0, _f = newTracker.generateActivityIndexList(); _e < _f.length; _e++) {
        var activityIndex = _f[_e];
        var indexedTracker = new IndexedTracker_1.IndexedTracker();
        indexedTracker.index = activityIndex.index;
        indexedTracker.surveyId = activityTrackingContract.surveyId;
        indexedTracker.tracker = newTracker;
        if (!newMap[activityIndex.activity]) {
          newMap[activityIndex.activity] = [];
        }
        // Register the new tracker in our new map
        newMap[activityIndex.activity].push(indexedTracker);
      }
    }
    if (duplicateCounter > 0) {
      var telemetryLogger = this.loggerCallback && this.loggerCallback();
      if (telemetryLogger) {
        telemetryLogger.log_Error(Constants_1.TelemetryEvent.SurveyActivity.SetActivityTrackingContracts.DuplicateSurveyID, "Duplicate surveyId passed in activityTrackingContracts. Duplicate count: " + duplicateCounter);
      }
    }
    // Set the new map in place of the old
    this.copyObject(this.trackedActivityMap, newMap);
  };
  SurveyActivityListener.prototype.clearSurveys = function () {
    var _this = this;
    Object.getOwnPropertyNames(this.trackedActivityMap).forEach(function (key) {
      delete _this.trackedActivityMap[key];
    });
  };
  SurveyActivityListener.prototype.saveSessionTrackingActivity = function (activity) {
    // Set the new activity in place of the old
    previousTrackedActivityMap[activity] = this.trackedActivityMap[activity];
  };
  // Get the indexedTracker that corresponds to the SurveyID and return that count. otherwise return 0.
  SurveyActivityListener.prototype.getCount = function (activity, surveyId) {
    var indexedTracker = this.getIndexedTracker(activity, surveyId);
    if (!indexedTracker) {
      return 0;
    }
    return indexedTracker.tracker.getCount(indexedTracker.index);
  };
  SurveyActivityListener.prototype.getSessionCount = function (activity, surveyId) {
    var indexedTracker = this.getIndexedTracker(activity, surveyId);
    if (!indexedTracker) {
      return 0;
    }
    return indexedTracker.tracker.getSessionCount(indexedTracker.index);
  };
  /**
   * An atomic get-and-set method.  Returns the current SessionCount, resetting it to zero and adding it into the established baseline
   */
  SurveyActivityListener.prototype.moveSessionCountIntoBaseCount = function (activity, surveyId) {
    var indexedTracker = this.getIndexedTracker(activity, surveyId);
    if (!indexedTracker) {
      return 0;
    }
    return indexedTracker.tracker.moveSessionCountIntoBaseCount(indexedTracker.index);
  };
  SurveyActivityListener.prototype.getSurveyIds = function (activity) {
    if (Utils_1.isNOU(activity)) {
      return undefined;
    }
    var indexedTrackers = this.trackedActivityMap[activity];
    if (!indexedTrackers) {
      return undefined;
    }
    var surveyIds = indexedTrackers.map(function (indexedTracker) {
      return indexedTracker.surveyId;
    });
    return surveyIds;
  };
  SurveyActivityListener.prototype.setCallback = function (callback) {
    this.callback = callback;
  };
  SurveyActivityListener.prototype.UpdateActivityTrackingContracts = function (surveyList) {
    if (Utils_1.isNOU(surveyList) || Object.keys(surveyList).length === 0) {
      this.clearSurveys();
      return;
    }
    for (var key in this.trackedActivityMap) {
      if (this.trackedActivityMap.hasOwnProperty(key)) {
        var tracker = this.trackedActivityMap[key];
        // loop through tracker and remove the ones not associate with given surveys
        for (var i = tracker.length - 1; i >= 0; --i) {
          if (surveyList.hasOwnProperty(tracker[i].surveyId)) {
            continue;
          }
          tracker.splice(i, 1);
        }
      }
    }
  };
  SurveyActivityListener.prototype.getIndexedTracker = function (activity, surveyId) {
    var indexedTrackers = this.trackedActivityMap[activity];
    if (!indexedTrackers) {
      return undefined;
    }
    var indexedTracker;
    for (var _i = 0, indexedTrackers_1 = indexedTrackers; _i < indexedTrackers_1.length; _i++) {
      var tracker = indexedTrackers_1[_i];
      if (tracker.surveyId === surveyId) {
        indexedTracker = tracker;
        break;
      }
    }
    return indexedTracker;
  };
  SurveyActivityListener.prototype.resetQueuedActivities = function () {
    queuedActivities = [];
  };
  SurveyActivityListener.prototype.logActivity_private = function (activityName, logType, increment, timestamp) {
    var _this = this;
    if (this.callback && !this.callback.shouldAcceptActivity(activityName)) {
      pendingActivityCount++;
      if (queuedActivities.length < SurveyActivityListener.MaxPendingActivitiesQueueSize) {
        queuedActivities.push({
          activityName: activityName,
          logType: logType,
          increment: increment,
          timestamp: timestamp
        });
      }
    }
    // Count any activities that are posted before 'FloodgateFirstStart' activity happens
    if (activityName === SurveyActivityListener.FloodgateStartActivityName) {
      if (pendingActivityCount > 0) {
        var telemetryLogger = this.loggerCallback && this.loggerCallback();
        if (telemetryLogger) {
          telemetryLogger.log_Event(Constants_1.TelemetryEvent.SurveyActivity.LogActivity.EventsReprocessed, {
            Count: queuedActivities.length,
            Dropped: pendingActivityCount - queuedActivities.length
          });
        }
        // replay all the queued activities one by one as the floodgate engine has started now
        queuedActivities.map(function (queuedActivity) {
          return _this.logActivity_core(queuedActivity.activityName, queuedActivity.logType, queuedActivity.increment, queuedActivity.timestamp);
        });
      }
      pendingActivityCount = 0;
      this.resetQueuedActivities();
    }
    this.logActivity_core(activityName, logType, increment, timestamp);
  };
  SurveyActivityListener.prototype.logActivity_core = function (activityName, logType, increment, timestamp) {
    var indexedTrackers = this.trackedActivityMap[activityName];
    if (!indexedTrackers || indexedTrackers.length === 0) {
      return;
    }
    var indices = [];
    // If we have more than one tracker shuffle trackers to randomize which is evaluated first
    if (indexedTrackers.length > 1) {
      indices = new Array(indexedTrackers.length);
      for (var i = 0; i < indices.length; i++) {
        indices[i] = i;
      }
      indices = Utils_1.fyShuffle(indices); // shuffle and reassign
    } else {
      indices.push(0); // just have a zero. Shuffling is not needed.
    }
    // Loop through trackers in shuffled order
    for (var _i = 0, indices_1 = indices; _i < indices_1.length; _i++) {
      var index = indices_1[_i];
      var tracker = indexedTrackers[index];
      switch (logType) {
        case 1 /* StartTime */:
          {
            tracker.tracker.startTime(tracker.index, timestamp);
            continue; // Look at the next tracker. Increment not needed.
          }
        case 2 /* StopTime */:
          {
            increment = tracker.tracker.stopTime(tracker.index, timestamp);
            // fall through to increment the activity as well.
          }
        case 0 /* Increment */:
          {
            break;
          }
        default:
          {
            continue;
          }
      }
      var result = tracker.tracker.incrementActivity(tracker.index, increment);
      if (result === 2 /* AllActivitiesActivated */) {
        // Check the activated surveys list to ensure whether any previous survey is still in process
        // If not, the length should be 1 and call to execute this survey right away
        // This should only happen if calling to Governance Service, due to the call is asynchronous
        this.executeCallback(tracker.surveyId);
        break;
        /* Breaking here to let one and only one callback to be made from a logActivity call.
        This ensures only one survey can fully 'trigger' for a logActivity call. Others that would also
        have triggered must wait until the next call to trigger (without there being a cooldown, of course).
        */
      }
    }
  };
  SurveyActivityListener.prototype.executeCallback = function (surveyId) {
    this.callback.run(surveyId);
  };
  SurveyActivityListener.prototype.copyObject = function (target, source) {
    Object.keys(source).forEach(function (key) {
      target[key] = source[key];
    });
  };
  SurveyActivityListener.FloodgateStartActivityName = "FloodgateFirstStart";
  // The size of the dropped activities is set to 100 due to the following two reasons.
  // 1) To prevent the array growing too big and causing memory issues.
  // 2) Based on the telemetry analysis of number of dropped activities, the count is very small and much lesser than 100.
  SurveyActivityListener.MaxPendingActivitiesQueueSize = 100;
  return SurveyActivityListener;
}();
module.exports = SurveyActivityListener;